import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { UserActions as CxUserActions } from '@spartacus/core';
import { Observable, of, zip } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { ConsentsConfig } from '../../../anonymous-consents/config/consents-config';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageActions } from '../../../global-message/store/actions';
import { Occ } from '../../../occ';
import { LanguagesActionTypes } from '../../../site-context/store/actions/languages.action';
import { normalizeHttpError, withdrawOn } from '../../../util';
import { AppActions } from '../../../util/store/actions';
import { UserConsentConnector } from '../../connectors/user-consent.connector';
import { UserConsentFacade } from '../../facades';
import { UserUtilService } from '../../services/user-util.service';
import { UserActions } from '../actions/index';
import { UserConsentsActionTypes } from '../actions/user-consents.action';

@Injectable()
export class UserConsentsEffect {
  private contextChange$ = this.actions$.pipe(ofType(LanguagesActionTypes.LanguageChange, LoginLogoutActionTypes.Logout));
  private logoutOnlyContextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  resetConsents$: Observable<CxUserActions.ResetLoadUserConsents> = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguagesActionTypes.LanguageChange, LoginLogoutActionTypes.Login, LoginLogoutActionTypes.Logout),
      map(() => new CxUserActions.ResetLoadUserConsents())
    )
  );

  getConsents$: Observable<CxUserActions.UserConsentsAction> = createEffect(() =>
    this.actions$.pipe(
      ofType<CxUserActions.LoadUserConsents>(CxUserActions.LOAD_USER_CONSENTS),
      map((action) => action.payload),
      concatMap((userId) =>
        this.userConsentConnector.loadConsents(userId).pipe(
          map((consents) => {
            const userConsents = consents.filter((template) => this.config.consents.user.consents.includes(template.id));
            return new CxUserActions.LoadUserConsentsSuccess(userConsents);
          }),
          catchError((error) => of(new CxUserActions.LoadUserConsentsFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  giveConsent$: Observable<
    UserActions.ConsentsActions | GlobalMessageActions.RemoveMessagesByType | UserActions.LoadAnalyticsMetadata
  > = createEffect(() =>
    this.actions$.pipe(
      ofType<CxUserActions.GiveUserConsent>(CxUserActions.GIVE_USER_CONSENT),
      concatMap((action) =>
        this.userConsentConnector
          .giveConsent(action.payload.userId, action.payload.consentTemplateId, action.payload.consentTemplateVersion)
          .pipe(
            mergeMap((consent) => {
              const actions = [];
              actions.push(new CxUserActions.GiveUserConsentSuccess(consent));
              if (consent.id === this.config.consents.user.trackingConsent) {
                actions.push(new UserActions.LoadAnalyticsMetadata(action.payload.userId));
              }
              return actions;
            }),
            catchError((error) => of(new CxUserActions.GiveUserConsentFail(normalizeHttpError(error))))
          )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  withdrawConsent$: Observable<UserActions.ConsentsActions | UserActions.LoadAnalyticsMetadata> = createEffect(() =>
    this.actions$.pipe(
      ofType(CxUserActions.WITHDRAW_USER_CONSENT),
      map((action: CxUserActions.WithdrawUserConsent) => action.payload),
      concatMap(({ userId, consentCode }) =>
        this.userConsentConnector.withdrawConsent(userId, consentCode).pipe(
          concatMap(() => {
            const actions = [];
            actions.push(new CxUserActions.WithdrawUserConsentSuccess());
            actions.push(new CxUserActions.LoadUserConsents(userId));
            if (consentCode === this.config.consents.user.trackingConsent) {
              actions.push(new UserActions.LoadAnalyticsMetadata(userId));
            }
            return actions;
          }),
          catchError((error) => of(new CxUserActions.WithdrawUserConsentFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  denyConsent$: Observable<UserActions.DenyUserConsentSuccess | UserActions.DenyUserConsentFail> = createEffect(() =>
    this.actions$.pipe(
      ofType<UserActions.DenyUserConsent>(UserConsentsActionTypes.DenyUserConsent),
      concatMap((action) =>
        this.userConsentConnector
          .denyConsent(action.payload.userId, action.payload.consentTemplateId, action.payload.consentTemplateVersion)
          .pipe(
            map((template) => new UserActions.DenyUserConsentSuccess(template)),
            catchError((error) => of(new UserActions.DenyUserConsentFail(normalizeHttpError(error))))
          )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleGetConsentsNotGiven$: Observable<AppActions.NoOpAction | UserActions.RequiredConsentsNotGiven> = createEffect(() =>
    this.actions$.pipe(
      ofType<CxUserActions.LoadUserConsentsSuccess>(CxUserActions.LOAD_USER_CONSENTS_SUCCESS),
      map(() =>
        this.userUtilService.getRequiredConsents().filter((c) => !this.config.consents.user.transferredConsents.includes(c))
      ),
      concatMap((requiredConsents) => zip(...requiredConsents.map((consent) => this.service.getConsent(consent)))),
      map((consentsData) => consentsData.filter((consent) => !this.userUtilService.isConsentGiven(consent))),
      map((consentsData) =>
        consentsData?.length ? new UserActions.RequiredConsentsNotGiven(consentsData) : new AppActions.NoOpAction()
      ),
      take(1),
      withdrawOn(this.contextChange$)
    )
  );

  showConsentBanner$: Observable<UserActions.SetConsentsBannerVisible> = createEffect(() =>
    this.actions$.pipe(
      ofType(CxUserActions.LOAD_USER_CONSENTS_SUCCESS),
      switchMap((action: CxUserActions.LoadUserConsentsSuccess) =>
        this.service.filterConsentTemplates(action.payload, [...this.userUtilService.getHiddenLoginConsents()])
      ),
      filter((optionalConsentTemplates: Occ.ConsentTemplate[]) => {
        return optionalConsentTemplates.reduce(
          (showBanner: boolean, template: Occ.ConsentTemplate): boolean =>
            showBanner || !this.userUtilService.isConsentAcknowledged(template),
          false
        );
      }),
      take(1),
      switchMap(() => [new UserActions.SetConsentsBannerVisible(true)]),
      withdrawOn(this.contextChange$)
    )
  );

  constructor(
    private actions$: Actions,
    private userConsentConnector: UserConsentConnector,
    private config: ConsentsConfig,
    private service: UserConsentFacade,
    private userUtilService: UserUtilService
  ) {}
}
